import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Save, Lock, Plus, Trash2, Video, History, PlugZap, Megaphone, TrendingUp, Target, Eye, MousePointerClick, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';
import { useAuthStore } from '../store';
import CampaignOptimizationsLog from './CampaignOptimizationsLog';
import { OPT_TIPOS, OPT_CONCLUSIONES } from '../lib/optimizations';
import { StaleDataNote } from './StaleDataNote';
import { AdsOptimizationSheetCard } from './AdsOptimizationSheetCard';

const uid = () => Math.random().toString(36).slice(2, 10);
const today = () => new Date().toISOString().slice(0, 10);
const ESTADOS = [
  { v: 'activa', label: 'Activa', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { v: 'pausada', label: 'Pausada', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  { v: 'finalizada', label: 'Finalizada', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
];
const newCampaign = () => ({ id: uid(), nombre: '', estado: 'activa', objetivo: '', presupuesto: '', fecha_inicio: '', fecha_fin: '', creatividades: '', notas: '', cambios: [] });
const fmtNum = (n) => Number(n || 0).toLocaleString('es-ES');
const fmtEur = (n) => `${Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`;

export default function AdsCampaignsSection({ clientId }) {
  const { user } = useAuthStore();
  const isCeo = user?.role === 'ceo';
  const [campanas, setCampanas] = useState([]);
  const [metaNotes, setMetaNotes] = useState({});
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Meta Ads
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [metaAvailable, setMetaAvailable] = useState(false);
  const [linkedAccountId, setLinkedAccountId] = useState('');
  const [metaData, setMetaData] = useState(null); // {linked, connected, summary, campaigns, message}
  const [savingLink, setSavingLink] = useState(false);
  const [metaMonth, setMetaMonth] = useState(() => new Date().toISOString().slice(0, 7));

  // Registro de optimizaciones (solo CEOs)
  const [opts, setOpts] = useState([]);
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterConc, setFilterConc] = useState('all');
  const loadOpts = useCallback(() => {
    if (!clientId || !isCeo) return;
    serviceDeliveryAPI.listCampaignOptimizations(clientId)
      .then((r) => setOpts(r?.optimizations || []))
      .catch(() => setOpts([]));
  }, [clientId, isCeo]);
  useEffect(() => { loadOpts(); }, [loadOpts]);
  const optsFor = (campId) => opts.filter((o) =>
    String(o.campaign_id) === String(campId) &&
    (filterTipo === 'all' || o.tipo === filterTipo) &&
    (filterConc === 'all' || (o.conclusion || 'pendiente') === filterConc));

  const loadMetaCampaigns = useCallback(async () => {
    setMetaData(null);
    try {
      const r = await serviceDeliveryAPI.getMetaCampaigns(clientId, metaMonth);
      setMetaData(r || { linked: false, connected: false, campaigns: [] });
    } catch { setMetaData({ linked: false, connected: false, campaigns: [] }); }
  }, [clientId, metaMonth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, client, accs] = await Promise.all([
        serviceDeliveryAPI.getLibrary(clientId, 'anuncios'),
        serviceDeliveryAPI.getClient(clientId).catch(() => ({})),
        serviceDeliveryAPI.getMetaAdAccounts().catch(() => ({ connected: false, accounts: [] })),
      ]);
      setCampanas(Array.isArray(res?.data?.campanas) ? res.data.campanas : []);
      setMetaNotes((res?.data?.meta_notes && typeof res.data.meta_notes === 'object') ? res.data.meta_notes : {});
      setCanEdit(!!res?.can_edit);
      setLinkedAccountId(client?.meta_ad_account_id || '');
      setMetaAccounts(accs?.accounts || []);
      setMetaAvailable(!!accs?.connected);
      setDirty(false);
    } catch { toast.error('Error cargando los anuncios'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { if (clientId) load(); }, [clientId, load]);
  useEffect(() => { if (clientId && linkedAccountId) loadMetaCampaigns(); else setMetaData(null); }, [clientId, linkedAccountId, loadMetaCampaigns]);

  const mutate = (next) => { setCampanas(next); setDirty(true); };
  const setC = (id, patch) => mutate(campanas.map((c) => c.id === id ? { ...c, ...patch } : c));
  const setNote = (campId, patch) => { setMetaNotes((prev) => ({ ...prev, [campId]: { ...(prev[campId] || {}), ...patch } })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try { await serviceDeliveryAPI.saveLibrary(clientId, 'anuncios', { campanas, meta_notes: metaNotes }); toast.success('Anuncios guardados'); setDirty(false); }
    catch (e) { toast.error(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const linkMeta = async (val) => {
    const accountId = val === '__none__' ? '' : val;
    setSavingLink(true);
    try {
      await serviceDeliveryAPI.setMetaAccount(clientId, accountId || null);
      setLinkedAccountId(accountId);
      toast.success(accountId ? 'Cuenta de Meta vinculada' : 'Cuenta de Meta desvinculada');
    } catch (e) { toast.error(e?.message || 'Error al vincular'); }
    finally { setSavingLink(false); }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const RO = !canEdit;

  const addCambio = (cId) => setC(cId, { cambios: [{ id: uid(), fecha: today(), descripcion: '' }, ...(campanas.find((c) => c.id === cId)?.cambios || [])] });

  return (
    <div className="space-y-5" data-testid="ads-campaigns-section">
      {isCeo && <AdsOptimizationSheetCard clientId={clientId} />}
      {/* Cabecera + guardar */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><Video className="w-5 h-5 text-primary" /> ADS</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Rendimiento en Meta (automático) + tus notas y campañas manuales.</p>
            </div>
            <div className="flex items-center gap-2">
              {RO ? <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge> : (
                <Button size="sm" onClick={handleSave} disabled={saving || !dirty} data-testid="ads-campaigns-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}{dirty ? 'Guardar' : 'Guardado'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {/* Vinculación de la cuenta de Meta */}
        <CardContent className="pt-0 space-y-3">
          {!metaAvailable ? (
            <div className="rounded-md border border-dashed p-2.5 flex items-center gap-2 text-xs text-muted-foreground" data-testid="ads-meta-pending">
              <PlugZap className="w-3.5 h-3.5 shrink-0" /> Meta Ads aún no está conectado. Cuando la cuenta de Meta esté conectada, aquí podrás vincular la cuenta publicitaria del cliente y las campañas tirarán de datos reales.
            </div>
          ) : (
            <div className="rounded-lg border p-3 space-y-2 bg-muted/10" data-testid="ads-meta-linker">
              <Label className="text-xs flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Cuenta publicitaria de Meta de este cliente</Label>
              {RO ? (
                <p className="text-sm">{metaAccounts.find((a) => String(a.id) === String(linkedAccountId))?.name || (linkedAccountId ? linkedAccountId : 'Sin vincular')}</p>
              ) : (
                <Select value={linkedAccountId || '__none__'} onValueChange={linkMeta} disabled={savingLink}>
                  <SelectTrigger className="max-w-md" data-testid="ads-meta-account-selector"><SelectValue placeholder="Selecciona una cuenta de Meta..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin vincular</SelectItem>
                    {metaAccounts.map((ac) => (<SelectItem key={ac.id} value={String(ac.id)}>{ac.name}{ac.active ? '' : ' (inactiva)'}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rendimiento en Meta (automático) */}
      {linkedAccountId && (
        <Card data-testid="ads-meta-performance">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2"><Megaphone className="w-4 h-4 text-primary" /> Rendimiento en Meta</CardTitle>
                <p className="text-xs text-muted-foreground">Datos automáticos de Meta Ads (solo lectura). Añade tus notas de equipo debajo de cada campaña.</p>
              </div>
              <input type="month" value={metaMonth} onChange={(e) => setMetaMonth(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm bg-background" data-testid="ads-meta-month" />
            </div>
            {isCeo && opts.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap w-full mt-2 text-xs">
                <span className="text-muted-foreground">Filtrar optimizaciones:</span>
                <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} className="h-7 rounded-md border px-1.5 text-xs bg-background" data-testid="opt-filter-tipo">
                  <option value="all">Todos los cambios</option>
                  {OPT_TIPOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
                <select value={filterConc} onChange={(e) => setFilterConc(e.target.value)} className="h-7 rounded-md border px-1.5 text-xs bg-background" data-testid="opt-filter-conclusion">
                  <option value="all">Todas las conclusiones</option>
                  {OPT_CONCLUSIONES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                  <option value="pendiente">Sin conclusión</option>
                </select>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {metaData === null ? (
              <div className="py-6 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : !metaData.connected ? (
              <div className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground" data-testid="ads-meta-not-connected">{metaData.message || 'Pendiente de conexión con Meta Ads.'}</div>
            ) : (
              <>
                <StaleDataNote data={metaData} testId="ads-meta-stale-note" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    isCeo && { icon: TrendingUp, label: 'Inversión', value: fmtEur(metaData.summary?.spend) },
                    { icon: Target, label: 'Leads', value: fmtNum(metaData.summary?.leads) },
                    isCeo && { icon: Video, label: 'Coste/lead', value: metaData.summary?.leads ? fmtEur(metaData.summary?.cost_per_lead) : '—' },
                    { icon: Eye, label: 'Impresiones', value: fmtNum(metaData.summary?.impressions) },
                  ].filter(Boolean).map((c) => (
                    <div key={c.label} className="rounded-lg border p-3">
                      <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] uppercase font-semibold"><c.icon className="w-3.5 h-3.5" /> {c.label}</div>
                      <p className="text-xl font-bold mt-0.5 tabular-nums">{c.value}</p>
                    </div>
                  ))}
                </div>
                {(!metaData.campaigns || metaData.campaigns.length === 0) ? (
                  <p className="text-sm text-muted-foreground text-center py-8" data-testid="ads-meta-campaigns-empty">No hay campañas con actividad este mes.</p>
                ) : metaData.campaigns.map((mc) => {
                  const note = metaNotes[mc.id] || {};
                  return (
                    <div key={mc.id} className="rounded-lg border p-3 space-y-3" data-testid={`meta-campaign-${mc.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold truncate">{mc.name}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {isCeo && <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {fmtEur(mc.spend)}</span>}
                          <span className="inline-flex items-center gap-1"><Target className="w-3 h-3" /> {fmtNum(mc.leads)} leads</span>
                          <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {fmtNum(mc.impressions)}</span>
                          <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {fmtNum(mc.clicks)}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div><Label className="text-xs">Objetivo (equipo)</Label><Input value={note.objetivo || ''} onChange={(e) => setNote(mc.id, { objetivo: e.target.value })} disabled={RO} placeholder="Leads, ventas, tráfico..." data-testid={`meta-note-objetivo-${mc.id}`} /></div>
                        <div><Label className="text-xs">Creatividades</Label><Input value={note.creatividades || ''} onChange={(e) => setNote(mc.id, { creatividades: e.target.value })} disabled={RO} placeholder="Qué creatividades lleva" /></div>
                      </div>
                      {note.notas && (
                        <p className="text-xs text-muted-foreground whitespace-pre-wrap"><span className="font-semibold">Notas antiguas: </span>{note.notas}</p>
                      )}
                      {isCeo && (
                        <CampaignOptimizationsLog
                          clientId={clientId}
                          campaign={mc}
                          entries={optsFor(mc.id)}
                          onChanged={loadOpts}
                        />
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Campañas manuales */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4 text-primary" /> Campañas manuales (bitácora)</CardTitle>
            {!RO && <Button variant="outline" size="sm" onClick={() => mutate([...campanas, newCampaign()])} data-testid="ads-add-campaign"><Plus className="w-4 h-4 mr-1" /> Añadir campaña</Button>}
          </div>
          <p className="text-xs text-muted-foreground">Para campañas que no están en Meta o para llevar tu histórico de cambios a mano.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {campanas.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="ads-campaigns-empty">Aún no hay campañas manuales.</div>
          ) : campanas.map((c) => {
            const est = ESTADOS.find((e) => e.v === c.estado) || ESTADOS[0];
            return (
              <div key={c.id} className="rounded-lg border p-3 space-y-3" data-testid={`ads-campaign-${c.id}`}>
                <div className="flex items-center gap-2">
                  <Input value={c.nombre} onChange={(e) => setC(c.id, { nombre: e.target.value })} disabled={RO} placeholder="Nombre de la campaña" className="h-9 font-medium" data-testid={`ads-campaign-nombre-${c.id}`} />
                  {RO ? <Badge variant="outline" className={est.cls}>{est.label}</Badge> : (
                    <select value={c.estado} onChange={(e) => setC(c.id, { estado: e.target.value })} className={`h-9 rounded-md border px-2 text-sm ${est.cls}`} data-testid={`ads-campaign-estado-${c.id}`}>
                      {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
                    </select>
                  )}
                  {!RO && <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 shrink-0" onClick={() => mutate(campanas.filter((x) => x.id !== c.id))} data-testid={`ads-del-campaign-${c.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Objetivo</Label><Input value={c.objetivo} onChange={(e) => setC(c.id, { objetivo: e.target.value })} disabled={RO} placeholder="Leads, ventas, tráfico, mensajes" data-testid={`ads-campaign-objetivo-${c.id}`} /></div>
                  {isCeo && <div><Label className="text-xs">Presupuesto</Label><Input value={c.presupuesto} onChange={(e) => setC(c.id, { presupuesto: e.target.value })} disabled={RO} placeholder="Ej: 300 €/mes" data-testid={`ads-campaign-presupuesto-${c.id}`} /></div>}
                  <div><Label className="text-xs">Fecha inicio</Label><Input type="date" value={c.fecha_inicio || ''} onChange={(e) => setC(c.id, { fecha_inicio: e.target.value })} disabled={RO} /></div>
                  <div><Label className="text-xs">Fecha fin</Label><Input type="date" value={c.fecha_fin || ''} onChange={(e) => setC(c.id, { fecha_fin: e.target.value })} disabled={RO} /></div>
                </div>
                <div><Label className="text-xs">Anuncios / creatividades que contiene</Label><Textarea value={c.creatividades} onChange={(e) => setC(c.id, { creatividades: e.target.value })} disabled={RO} rows={2} className="resize-none" /></div>
                <div><Label className="text-xs">Notas</Label><Textarea value={c.notas} onChange={(e) => setC(c.id, { notas: e.target.value })} disabled={RO} rows={2} className="resize-none" /></div>

                <div className="rounded-lg border p-3 bg-muted/20">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs flex items-center gap-1"><History className="w-3.5 h-3.5" /> Registro de cambios</Label>
                    {!RO && <Button variant="outline" size="sm" onClick={() => addCambio(c.id)} data-testid={`ads-add-cambio-${c.id}`}><Plus className="w-3.5 h-3.5 mr-1" /> Apuntar cambio</Button>}
                  </div>
                  {(c.cambios || []).length === 0 ? <p className="text-xs text-muted-foreground italic">Sin cambios registrados.</p> : (
                    <div className="space-y-2">
                      {(c.cambios || []).map((ch) => (
                        <div key={ch.id} className="flex items-start gap-2" data-testid={`ads-cambio-${ch.id}`}>
                          <Input type="date" value={ch.fecha || ''} onChange={(e) => setC(c.id, { cambios: c.cambios.map((x) => x.id === ch.id ? { ...x, fecha: e.target.value } : x) })} disabled={RO} className="h-8 w-36 shrink-0" />
                          <Input value={ch.descripcion} onChange={(e) => setC(c.id, { cambios: c.cambios.map((x) => x.id === ch.id ? { ...x, descripcion: e.target.value } : x) })} disabled={RO} placeholder="Subí presupuesto, cambié creatividad, pausé…" className="h-8" data-testid={`ads-cambio-desc-${ch.id}`} />
                          {!RO && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => setC(c.id, { cambios: c.cambios.filter((x) => x.id !== ch.id) })}><Trash2 className="w-3.5 h-3.5" /></Button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
